/**
 * Atmospheric particle wisps around the planet using three.quarks.
 * Slow-orbiting translucent particles in a thin shell above the surface.
 */

import * as THREE from 'three';
import {
  ParticleSystem,
  SphereEmitter,
  ConstantValue,
  IntervalValue,
  PiecewiseBezier,
  Bezier,
  ConstantColor,
  ColorOverLife,
  SizeOverLife,
  OrbitOverLife,
  Gradient,
  RenderMode,
  Vector3 as QVector3,
  Vector4,
} from 'three.quarks';
import type { Vector4 as QVector4 } from 'three.quarks';
import type { AtmosphereWispsConfig } from '@warmancer/shared';

/**
 * Creates atmospheric wisp particles orbiting the planet.
 * @param planetRadius - planet sphere radius in world units
 */
export function createAtmosphereWisps(planetRadius: number): ParticleSystem {
  const smokeTexture = new THREE.TextureLoader().load('/assets/particles/smoke_01.png');
  smokeTexture.colorSpace = THREE.SRGBColorSpace;

  const color = new Vector4(0.3, 0.5, 1.0, 0.15);

  const colorGradient = new Gradient(
    [
      [new QVector3(0.3, 0.5, 1.2), 0],     // blue-white HDR start
      [new QVector3(0.2, 0.4, 1.0), 0.5],   // blue mid
      [new QVector3(0.1, 0.2, 0.6), 1.0],   // dim blue end
    ],
    [[0, 0], [0.15, 0.15], [0.15, 0.5], [0, 1.0]]  // fade in, hold, fade out
  );

  return new ParticleSystem({
    duration: 0,
    looping: true,
    shape: new SphereEmitter({
      radius: planetRadius * 1.1,
      thickness: 0.1,
    }),
    startLife: new IntervalValue(3, 6),
    startSpeed: new ConstantValue(0.1),
    startSize: new IntervalValue(1.0, 4.0),
    startColor: new ConstantColor(color as QVector4),
    emissionOverTime: new ConstantValue(15),
    worldSpace: false,
    renderMode: RenderMode.BillBoard,
    material: new THREE.MeshBasicMaterial({
      map: smokeTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
    behaviors: [
      new OrbitOverLife(new ConstantValue(0.2), new QVector3(0, 1, 0)),
      new SizeOverLife(new PiecewiseBezier([[new Bezier(0.5, 1, 1, 0.5), 0]])),
      new ColorOverLife(colorGradient),
    ],
  });
}

/**
 * Creates atmosphere wisps from an AtmosphereWispsConfig.
 * This is the config-driven version used by the particle editor and store.
 * @param planetRadius - planet sphere radius in world units
 * @param config - atmosphere wisps configuration
 */
export function createAtmosphereWispsFromConfig(
  planetRadius: number,
  config: AtmosphereWispsConfig,
): ParticleSystem {
  const texture = new THREE.TextureLoader().load(config.texture);
  texture.colorSpace = THREE.SRGBColorSpace;

  const [sr, sg, sb, sa] = config.startColor;
  const startColor = new Vector4(sr, sg, sb, sa);

  const colorGradient = new Gradient(
    config.colorStops.map(([rgb, pos]) => [new QVector3(rgb[0], rgb[1], rgb[2]), pos] as [QVector3, number]),
    config.alphaKeys as [number, number][],
  );

  const [s0, s1, s2, s3] = config.sizeCurve;

  return new ParticleSystem({
    duration: 0,
    looping: true,
    shape: new SphereEmitter({
      radius: planetRadius * config.radiusMultiplier,
      thickness: config.thickness,
    }),
    startLife: new IntervalValue(config.lifetimeMin, config.lifetimeMax),
    startSpeed: new ConstantValue(config.startSpeed),
    startSize: new IntervalValue(config.sizeMin, config.sizeMax),
    startColor: new ConstantColor(startColor as QVector4),
    emissionOverTime: new ConstantValue(config.emissionRate),
    worldSpace: false,
    renderMode: RenderMode.BillBoard,
    material: new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
    behaviors: [
      new OrbitOverLife(new ConstantValue(config.orbitSpeed), new QVector3(0, 1, 0)),
      new SizeOverLife(new PiecewiseBezier([[new Bezier(s0, s1, s2, s3), 0]])),
      new ColorOverLife(colorGradient),
    ],
  });
}
